// FormComponent.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Paper,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { X } from "lucide-react";
import Loader from "../Components/Loader";
import axiosInstance from "../axiosinstance";

/** Header inputs row (read-only) — Bootstrap 5 Layout */
function HeaderRow({ headerFields = [], headerData = {} }) {
  return (
    <Paper
      component="section"
      aria-label="Equipment summary"
      variant="outlined"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${Math.max(headerFields.length, 1)}, minmax(0, 1fr))`,
        },
        mb: 2,
        overflow: "hidden",
        borderColor: "#d7dce2",
        borderRadius: 1.5,
        bgcolor: "#f8fafc",
      }}
    >
      {headerFields.map((hf, i) => (
        <Box
          key={hf.name || i}
          sx={{
            minWidth: 0,
            px: 2,
            py: 1,
            borderLeft: {
              xs: 0,
              sm: i % 2 === 0 ? 0 : "1px solid #e2e6ea",
              md: i === 0 ? 0 : "1px solid #e2e6ea",
            },
            borderTop: {
              xs: i === 0 ? 0 : "1px solid #e2e6ea",
              sm: i < 2 ? 0 : "1px solid #e2e6ea",
              md: 0,
            },
          }}
        >
          <Typography
            component="div"
            sx={{
              mb: 0.25,
              color: "#667085",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {hf.label}
          </Typography>
          <Typography
            component="div"
            title={String(headerData?.[hf.name] ?? "")}
            sx={{
              overflow: "hidden",
              color: "#1f2937",
              fontSize: 14,
              fontWeight: 600,
              fontStyle: hf.name === "signed" ? "italic" : "normal",
              lineHeight: 1.35,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {headerData?.[hf.name] || "—"}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

function inferPreviewKind({ filePath = "", mime = "" }) {
  const path = (filePath || "").toLowerCase();
  const m = (mime || "").toLowerCase();
  const isImg =
    /\.(png|jpe?g|webp|gif|bmp)$/i.test(path) || m.startsWith("image/");
  const isPdf = /\.pdf$/i.test(path) || m === "application/pdf";
  if (isImg) return "image";
  if (isPdf) return "pdf";
  return "other";
}

const thumbUrl = (API_URL, filePath = "") => {
  if (!filePath) return "";

  const cleaned = filePath.replace(/\\/g, "/").replace(/^\/+/, "");

  // ✅ if backend sends "uploads/..." then DIRECTLY use it (NO encoding)
  if (cleaned.startsWith("uploads/")) {
    // ✅ remove "uploads/" prefix
    return `${API_URL}/view_image/${cleaned.replace(/^uploads\//, "")}`;
  }

  const parts = cleaned.split("/").filter(Boolean);

  // case: folder/file.jpg
  if (parts.length === 2) {
    return `${API_URL}/view_image/${parts[0]}/${parts[1]}`;
  }

  // case: only file.jpg
  if (parts.length === 1) {
    return `${API_URL}/view_image/uploads/${parts[0]}`;
  }

  // ✅ IMPORTANT CHANGE:
  // for folder/uuid/filename.jpg => DO NOT encode full filename because it contains / sometimes
  if (parts.length >= 3) {
    return `${API_URL}/view_image/${parts.join("/")}`;
  }

  return "";
};

/** Existing file card (server file) */
function ExistingFileTile({
  file,
  API_URL,
  onView,
  onDownload,
  onRemoveExisting,
  isEditMode,
}) {
  const serverPath = file?.file_path || "";
  // const url = `${API_URL}/${serverPath.replace(/\\/g, "/")}`;

  const url = thumbUrl(API_URL, serverPath);

  const name = file?.filename || "file";
  const kind = inferPreviewKind({ filePath: file?.file_path });
  const isNewFile = file?.is_new === true;

  const [thumbSrc, setThumbSrc] = useState(null);

  useEffect(() => {
    if (kind !== "image" || !url || isNewFile) return;

    let alive = true;
    let objectUrl;

    (async () => {
      try {
        const token = localStorage.getItem("authKey");
        const res = await axiosInstance.get(url, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        objectUrl = URL.createObjectURL(res.data);
        if (alive) setThumbSrc(objectUrl);
      } catch (err) {
        console.error("Thumbnail load failed:", err);
      }
    })();

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, kind, isNewFile]);

  return (
    <Paper
      variant="outlined"
      sx={{ width: 160, height: 190, position: "relative", overflow: "hidden" }}
    >
      {/* Remove icon only while editing */}
      {isEditMode && (
        <IconButton
          size="small"
          aria-label="remove"
          onClick={() => onRemoveExisting?.(file)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 2,
            bgcolor: "rgba(0,0,0,0.55)",
            color: "white",
            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={{
          width: "100%",
          height: 120,
          bgcolor: "#f2f2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isNewFile ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#e8f5e8",
            }}
          >
            <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
              ✓ Uploaded
            </Typography>
            <Typography variant="caption" sx={{ px: 1, textAlign: "center" }}>
              {name}
            </Typography>
          </Box>
        ) : kind === "image" && thumbSrc ? (
          <img
            src={thumbSrc}
            alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography variant="h4">📄</Typography>
        )}
      </Box>
      {!isNewFile && (
        <Box sx={{ px: 1, pt: 0.75 }}>
          <Typography variant="caption" noWrap title={name}>
            {name}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          bgcolor: "rgba(0,0,0,0.55)",
        }}
      >
        {!isNewFile && (
          <Tooltip title="View">
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={async () => {
                try {
                  const token = localStorage.getItem("authKey");

                  const res = await axiosInstance.get(url, {
                    responseType: "blob",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  });

                  const blobUrl = URL.createObjectURL(res.data);

                  onView({
                    url: blobUrl,
                    name,
                    kind,
                    isObjectUrl: true,
                  });
                } catch (e) {
                  console.error("Failed to load image preview", e);
                }
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isNewFile && (
          <Tooltip title="Download">
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDownload({
                  serverPath: file?.file_path, // <— important
                  name: file?.filename || "download",
                });
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}

/** Preview tile for newly picked files (client side) */
function UploadTile({ file, onRemove, onView, onDownload }) {
  const objectUrl = React.useMemo(() => URL.createObjectURL(file), [file]);
  const name = file.name;
  const kind = inferPreviewKind({ mime: file.type });

  // Revoke only when this tile unmounts
  useEffect(() => {
    return () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {}
    };
  }, [objectUrl]);

  return (
    <Paper
      variant="outlined"
      sx={{ width: 160, height: 190, position: "relative", overflow: "hidden" }}
    >
      <Box
        sx={{
          width: "100%",
          height: 120,
          bgcolor: "#f2f2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {kind === "image" ? (
          <img
            src={objectUrl}
            alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography variant="h4">📄</Typography>
        )}
      </Box>
      <Box sx={{ px: 1, pt: 0.75 }}>
        <Typography variant="caption" noWrap title={name}>
          {name}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          bgcolor: "rgba(0,0,0,0.55)",
        }}
      >
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={() =>
                onView({ url: objectUrl, name, kind, isObjectUrl: true })
              }
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDownload({ url: objectUrl, name }); // local object URL
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Remove">
          <IconButton size="small" sx={{ color: "#fff" }} onClick={onRemove}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}

/** Upload dashed box + modal viewer */
function UploadBox({
  files = [],
  existingFiles = [],
  onFilesChange,
  disabled,
  API_URL,
  accept = "image/*,application/pdf",
  helpText,
  isEditMode = false,
  onRemoveExistingFile,
  isGauge = false,
  multiple = true, // <-- new prop (honored unless isGauge)
  maxFiles = 10,
  maxSizeMB = 5,
  error = false,
  errorText = "",
}) {
  const safeFiles = Array.isArray(files) ? files : [];
  const safeExisting = Array.isArray(existingFiles) ? existingFiles : [];
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionError, setSelectionError] = useState("");

  // Determine if gauge photo is already uploaded
  const hasGaugePhoto =
    isGauge && (safeFiles.length > 0 || safeExisting.length > 0);

  const acceptedTypes = accept.split(",").map((type) => type.trim());
  const isAcceptedFile = (file) =>
    acceptedTypes.some((type) => {
      if (type === "image/*") return file.type.startsWith("image/");
      if (type.startsWith("."))
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      return file.type === type;
    });

  const applyPickedFiles = (incoming) => {
    const allowedCount = isGauge || !multiple ? 1 : maxFiles;
    const validTypeFiles = incoming.filter(isAcceptedFile);
    const validSizeFiles = validTypeFiles.filter(
      (file) => file.size <= maxSizeMB * 1024 * 1024,
    );
    const merged = isGauge || !multiple
      ? validSizeFiles.slice(0, 1)
      : [...safeFiles, ...validSizeFiles].slice(0, allowedCount);

    if (validTypeFiles.length !== incoming.length) {
      setSelectionError("Some files were skipped because their type is not supported.");
    } else if (validSizeFiles.length !== validTypeFiles.length) {
      setSelectionError(`Each file must be ${maxSizeMB} MB or smaller.`);
    } else if (!isGauge && multiple && safeFiles.length + validSizeFiles.length > allowedCount) {
      setSelectionError(`You can select a maximum of ${allowedCount} files.`);
    } else {
      setSelectionError("");
    }

    if (merged.length || (isGauge && validSizeFiles.length)) {
      onFilesChange(merged);
    }
  };

  const [viewer, setViewer] = useState({
    open: false,
    url: "",
    name: "",
    kind: "image",
    isObjectUrl: false,
  });

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (!droppedFiles.length) return;

    applyPickedFiles(droppedFiles);
  };

  const openViewer = ({ url, name, kind, isObjectUrl }) =>
    setViewer({ open: true, url, name, kind, isObjectUrl: !!isObjectUrl });

  const closeViewer = () =>
    setViewer((v) => {
      if (v.isObjectUrl && v.url) {
        try {
          URL.revokeObjectURL(v.url);
        } catch {}
      }
      return {
        open: false,
        url: "",
        name: "",
        kind: "image",
        isObjectUrl: false,
      };
    });

  // helper: try to read a filename from Content-Disposition or serverPath
  const pickFilename = (res, fallback) => {
    const cd = res?.headers?.["content-disposition"] || "";
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    if (m) return decodeURIComponent(m[1] || m[2] || "").trim();
    return fallback || "download";
  };

  const buildDownloadUrl = (API_URL, filePath = "") => {
    if (!filePath) return "";

    const cleaned = filePath.replace(/\\/g, "/").replace(/^\/+/, "");

    if (cleaned.startsWith("uploads/")) {
      return `${API_URL}/download_image/${cleaned.replace(/^uploads\//, "")}`;
    }
    const parts = cleaned.split("/").filter(Boolean);

    if (parts.length === 2) {
      return `${API_URL}/download_image/${parts[0]}/${parts[1]}`;
    }

    if (parts.length === 1) {
      return `${API_URL}/download_image/uploads/${parts[0]}`;
    }

    if (parts.length >= 3) {
      return `${API_URL}/download_image/${parts.join("/")}`;
    }

    return "";
  };

  // ✅ download without navigation, with auth when needed
  const onDownloadOnly = async ({ url, name, serverPath }) => {
    try {
      // If it's a server file, fetch as blob WITH Authorization
      if (serverPath) {
        const token = localStorage.getItem("authKey") || "";
        const finalUrl = buildDownloadUrl(API_URL, serverPath);

        const res = await axiosInstance.get(finalUrl, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const filename =
          pickFilename(res, name) ||
          serverPath.split(/[\\/]/).pop() ||
          "download";

        const blobUrl = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
        return;
      }

      // If it's a local object URL (new upload), just trigger <a download>
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "download";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) {
      e.target.value = "";
      return;
    }

    // Debug: Log file details
    if (isGauge && picked[0]) {
      console.log("🔍 Gauge Photo Selected:");
      console.log("  - Name:", picked[0].name);
      console.log("  - Size:", picked[0].size, "bytes");
      console.log("  - Type:", picked[0].type);
      console.log("  - Last Modified:", new Date(picked[0].lastModified));

      // Validate file can be read
      const reader = new FileReader();
      reader.onload = (event) => {
        console.log(
          "🔍 File successfully read, size:",
          event.target.result.length,
          "bytes",
        );
      };
      reader.onerror = () => {
        console.error("❌ ERROR: Failed to read the file");
      };
      reader.readAsArrayBuffer(picked[0]);
    }

    applyPickedFiles(picked);
    e.target.value = "";
  };

  const onRemove = (idx) => {
    const list = Array.isArray(files) ? files : [];
    onFilesChange(list.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {/* Dashed Box */}
      <Paper
        variant="outlined"
        sx={{
          borderWidth: 2,
          borderStyle: "dashed",
          // if gauge photo, use green tones if uploaded, red tones if not; otherwise keep previous palette
          borderColor: error
            ? "#d32f2f"
            : isGauge
            ? hasGaugePhoto
              ? isDragging
                ? "#2e7d32"
                : "#4caf50"
              : isDragging
                ? "#d32f2f"
                : "#f44336"
            : isDragging
              ? "#c91f17"
              : disabled
                ? "#e0e0e0"
                : "#e1261c",
          borderRadius: 2,
          p: { xs: 1.25, sm: 2 },
          minHeight: { xs: "auto", sm: 116 },
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          bgcolor: isGauge
            ? hasGaugePhoto
              ? isDragging
                ? "rgba(46, 125, 50, 0.06)"
                : "#f1f8e9"
              : isDragging
                ? "rgba(211, 47, 47, 0.06)"
                : "#fff7f7"
            : isDragging
              ? "rgba(225, 38, 28, 0.08)"
              : disabled
                ? "#fafafa"
                : "#f9f9f9",
          opacity: disabled ? 0.6 : 1,
          "&:hover": disabled
            ? {}
            : isGauge
              ? hasGaugePhoto
                ? { bgcolor: "#e8f5e9", borderColor: "#2e7d32" }
                : { bgcolor: "#fff2f2", borderColor: "#d32f2f" }
              : { bgcolor: "#fff5f4", borderColor: "#c91f17" },
          transition: "all 0.2s ease",
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={!isGauge && !!multiple}
          accept={accept}
          style={{ display: "none" }}
          onChange={onPick}
          disabled={disabled}
        />
        <UploadIcon
          sx={{
            fontSize: { xs: 32, sm: 44 },
            color: isGauge
              ? hasGaugePhoto
                ? isDragging
                  ? "#2e7d32"
                  : "#4caf50"
                : isDragging
                  ? "#d32f2f"
                  : "#f44336"
              : isDragging
                ? "#c91f17"
                : "#e1261c",
            mb: 1,
            transition: "color 0.2s ease",
          }}
        />
        <Typography
          variant="body2"
          fontWeight="700"
          sx={{
            color: isGauge
              ? hasGaugePhoto
                ? "success.main"
                : "error.main"
              : "text.primary",
          }}
        >
          Drag & Drop or Click to Upload
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          Images or PDF • {isGauge || !multiple ? "1 file" : `up to ${maxFiles} files`} • {maxSizeMB} MB each
        </Typography>
        {helpText && (
          <Typography
            variant="caption"
            color={
              isGauge
                ? hasGaugePhoto
                  ? "success.main"
                  : "error.main"
                : "text.secondary"
            }
            sx={{ display: "block", mt: 0.5 }}
          >
            {helpText}
          </Typography>
        )}
        {selectionError && (
          <Typography
            role="alert"
            variant="caption"
            color="error.main"
            sx={{ display: "block", mt: 0.5, fontWeight: 600 }}
          >
            {selectionError}
          </Typography>
        )}
        {error && errorText && (
          <Typography
            role="alert"
            variant="caption"
            color="error.main"
            sx={{ display: "block", mt: 0.5, fontWeight: 600 }}
          >
            {errorText}
          </Typography>
        )}
      </Paper>

      {/* Existing server files + new uploads */}
      {(existingFiles.length > 0 || files.length > 0) && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            {isGauge ? "Uploaded Gauge Photo" : "Uploaded Photos"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              overflowY: "hidden",
              pb: 2, // Add padding bottom to show scrollbar
              // Hide scrollbar for webkit browsers
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#666",
                },
              },
              // For Firefox
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            {safeExisting.map((ef, i) => (
              <Box sx={{ flexShrink: 0 }}>
                {" "}
                {/* Prevent thumbnail shrinking */}
                <ExistingFileTile
                  key={`ex-${i}`}
                  file={ef}
                  API_URL={API_URL}
                  onView={openViewer}
                  onDownload={onDownloadOnly}
                  onRemoveExisting={onRemoveExistingFile}
                  isEditMode={isEditMode}
                />
              </Box>
            ))}
            {safeFiles.map((f, i) => (
              <Box sx={{ flexShrink: 0 }}>
                {" "}
                {/* Prevent thumbnail shrinking */}
                <UploadTile
                  key={`up-${i}`}
                  file={f}
                  onRemove={() => onRemove(i)}
                  onView={openViewer}
                  onDownload={onDownloadOnly}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Dialog
        open={viewer.open}
        onClose={closeViewer}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { position: "relative", borderRadius: 2 } }}
      >
        <IconButton
          onClick={closeViewer}
          sx={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#111",
          }}
        >
          {viewer.kind === "image" && (
            <img
              src={viewer.url}
              alt={viewer.name}
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: "80vh",
                display: "block",
              }}
            />
          )}
          {viewer.kind === "pdf" && (
            <iframe
              title={viewer.name}
              src={viewer.url}
              style={{
                width: "100%",
                height: "80vh",
                border: 0,
                background: "#fff",
              }}
            />
          )}
          {viewer.kind === "other" && (
            <Box
              sx={{ color: "#fff", p: 3, textAlign: "center", width: "100%" }}
            >
              <Typography variant="h6" gutterBottom>
                Preview not available
              </Typography>
              <Typography variant="body2">
                Use the <strong>Download</strong> button to save and view this
                file.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/** Dynamic field → MUI input (Bootstrap 5 Grid layout) */
function RenderGridField({
  def,
  values,
  onChange,
  onFieldBlur,
  disabled,
  API_URL,
  isEditMode,
  errors = {},
  fieldAssistMessages = {},
  lockTestResultOnly = false,
  testingOngoing = false,
}) {
  const hasError = !!errors?.[def.name];
  const helper = hasError ? errors[def.name] : undefined;
  const assistConfig = fieldAssistMessages?.[def.name];
  const assistText =
    !hasError && assistConfig?.text ? String(assistConfig.text) : undefined;
  const isWarningAssist = assistConfig?.severity === "warning";
  const isPassField = /^pass[1-4]$/.test(def.name || "");

  // Disable only test_result when lockTestResult=true (edit_testing_form case)
  const combinedDisabled =
    disabled || (lockTestResultOnly && def.name === "test_result");

  if (typeof def.visibleWhen === "function" && !def.visibleWhen(values || {}))
    return null;

  // ✅ Evaluate label and required as functions if needed
  const evaluatedLabel = typeof def.label === "function" ? def.label(values || {}) : def.label;
  

  if (def.type === "multiline") {
    return (
      <TextField
        fullWidth
        multiline
        minRows={def.rows}
        label={evaluatedLabel}
        value={values?.[def.name] || ""}
        onChange={(e) => onChange(def.name, e.target.value)}
        onBlur={(e) => onFieldBlur?.(def.name, e.target.value)}
        disabled={combinedDisabled}
        size="small"
        error={hasError}
        helperText={helper || assistText}
        FormHelperTextProps={
          assistText && isWarningAssist
            ? {
                sx: {
                  color: "#b45309",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "0.68rem",
                  lineHeight: 1.2,
                },
              }
            : undefined
        }
      />
    );
  }

  if (def.type === "text") {
    return (
      <TextField
        fullWidth
        label={evaluatedLabel}
        value={values?.[def.name] || ""}
        onChange={(e) => onChange(def.name, e.target.value)}
        onBlur={(e) => onFieldBlur?.(def.name, e.target.value)}
        disabled={combinedDisabled}
        size="small"
        error={hasError}
        helperText={helper || assistText}
        FormHelperTextProps={
          assistText && isWarningAssist
            ? {
                sx: {
                  color: "#b45309",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "0.68rem",
                  lineHeight: 1.2,
                },
              }
            : undefined
        }
      />
    );
  }

  if (def.type === "number") {
    return (
      <TextField
        fullWidth
        type="number"
        label={evaluatedLabel}
        value={values?.[def.name] ?? ""}
        onChange={(e) => {
          const nextValue = e.target.value;
          if (isPassField && nextValue !== "" && !/^\d+$/.test(nextValue)) {
            return;
          }
          onChange(def.name, nextValue);
        }}
        onKeyDown={(e) => {
          if (
            isPassField &&
            ["-", "+", ".", "e", "E"].includes(e.key)
          ) {
            e.preventDefault();
          }
        }}
        onBlur={(e) => onFieldBlur?.(def.name, e.target.value)}
        disabled={combinedDisabled}
        size="small"
        error={hasError}
        helperText={helper || assistText}
        FormHelperTextProps={
          assistText && isWarningAssist
            ? {
                sx: {
                  color: "#b45309",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "0.68rem",
                  lineHeight: 1.2,
                },
              }
            : undefined
        }
        InputProps={{
          inputProps: isPassField
            ? { min: 0, step: 1, inputMode: "numeric", pattern: "[0-9]*" }
            : undefined,
          endAdornment: def.adornEnd ? (
            <InputAdornment position="end">{def.adornEnd}</InputAdornment>
          ) : null,
        }}
      />
    );
  }

  if (def.type === "select") {
    const isTestResultSelect = def.name === "test_result";

    return (
      <TextField
        select
        fullWidth
        label={evaluatedLabel}
        value={values?.[def.name] ?? ""}
        onChange={(e) => onChange(def.name, e.target.value)}
        onBlur={(e) => onFieldBlur?.(def.name, e.target.value)}
        disabled={combinedDisabled}
        size="small"
        error={hasError}
        helperText={helper}
        SelectProps={
          isTestResultSelect
            ? {
                renderValue: (selected) => {
                  const option = (def.options || []).find(
                    (item) => item.value === selected,
                  );
                  const isPass = String(selected).toUpperCase() === "PASS";
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: isPass ? "#2e7d32" : "#d32f2f",
                        }}
                      />
                      {option?.label || selected}
                    </Box>
                  );
                },
                MenuProps: {
                  PaperProps: {
                    elevation: 6,
                    sx: {
                      mt: 0.75,
                      border: "1px solid #e5e7eb",
                      borderRadius: 2,
                      overflow: "hidden",
                      "& .MuiList-root": { p: 0.75 },
                    },
                  },
                },
              }
            : undefined
        }
      >
        {(def.options || []).map((opt) => {
          const isPass = String(opt.value).toUpperCase() === "PASS";
          return (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={
              isTestResultSelect
                ? {
                    minHeight: 42,
                    gap: 1.25,
                    borderRadius: 1.25,
                    fontSize: 15,
                    "&:hover": { bgcolor: "#fff3f2" },
                    "&.Mui-selected": {
                      bgcolor: "#fde8e6",
                      color: "#9f1d14",
                      fontWeight: 600,
                    },
                    "&.Mui-selected:hover": { bgcolor: "#fbd9d6" },
                  }
                : undefined
            }
          >
            {isTestResultSelect && (
              <Box
                component="span"
                sx={{
                  width: 9,
                  height: 9,
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  bgcolor: isPass ? "#2e7d32" : "#d32f2f",
                }}
              />
            )}
            {opt.label}
          </MenuItem>
          );
        })}
      </TextField>
    );
  }

  if (def.type === "checkbox") {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!values?.[def.name]}
            onChange={(e) => onChange(def.name, e.target.checked)}
            disabled={combinedDisabled}
            size="small"
          />
        }
        label={evaluatedLabel}
      />
    );
  }

  if (def.type === "upload") {
    const fieldVal = Array.isArray(values?.[def.name]) ? values[def.name] : [];
    // const existing = values?.__existingFiles || [];
    const isGauge = def.name === "__gauge_photo";
    const existing = isGauge
      ? values?.__existingGauge || []
      : values?.__existingUploads || [];

    // Determine the appropriate help text for gauge photo
    let helpText = def.helpText;
    if (isGauge) {
      if (existing.length > 0) {
        helpText = "Gauge photo uploaded ✓";
      } else if (fieldVal.length > 0) {
        helpText = "New gauge photo selected ✓";
      } else {
        helpText = "* Gauge photo is required for testing";
      }
    }

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {evaluatedLabel}
        </Typography>
        <UploadBox
          files={fieldVal}
          existingFiles={existing}
          onFilesChange={(f) => onChange(def.name, f)}
          disabled={combinedDisabled}
          API_URL={API_URL}
          accept={def.accept || "image/*,application/pdf"}
          helpText={helpText}
          isEditMode={isEditMode}
          onRemoveExistingFile={(file) => {
            // 1) push id into section-level __removeExistingIds array
            const prev = Array.isArray(values?.__removeExistingIds)
              ? values.__removeExistingIds
              : [];
            const next = [...prev, String(file?.file_id ?? file?.id)];
            onChange("__removeExistingIds", next);

            // 2) remove the tile from the right existing-files array so UI updates
            if (isGauge) {
              const now = Array.isArray(values?.__existingGauge)
                ? values.__existingGauge
                : [];
              onChange(
                "__existingGauge",
                now.filter((x) => x.file_id !== file.file_id),
              );
            } else if (Array.isArray(values?.__existingUploads)) {
              const now = values.__existingUploads;
              onChange(
                "__existingUploads",
                now.filter((x) => x.file_id !== file.file_id),
              );
            } else {
              const now = Array.isArray(values?.__existingFiles)
                ? values.__existingFiles
                : [];
              onChange(
                "__existingFiles",
                now.filter((x) => x.file_id !== file.file_id),
              );
            }
          }}
          isGauge={isGauge}
          multiple={!!def.multiple}
          error={hasError}
          errorText={helper}
        />
      </Box>
    );
  }

  return (
    <TextField
      className="col-4"
      label={def.label}
      value={values?.[def.name] || ""}
      onChange={(e) => onChange(def.name, e.target.value)}
      disabled={combinedDisabled}
      size="small"
    />
  );
}

/**
 * FormComponent — old UI layout, schema-driven.
 */
export default function FormComponent({
  schema,
  headerData,
  values,
  onChange,
  onFieldBlur,
  isCurrentForm,
  isEditMode,
  onEditModeChange,
  onSubmit,
  actionButtonLabel,
  onActionButton,
  API_URL,
  forceDisabled = false,
  hideSubmit = false,
  hasSavedData = false, // indicates if form has saved snapshot
  hideEditButton = false,
  errors = {},
  fieldAssistMessages = {},
  lockTestResultOnly = false,
  testingOngoing = false,
  backendCurrentForm,
  onCancelEdit = () => {},
  onLockedInteraction,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingLoading, setIsEditingLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // wrappers that show loader and call the passed handlers (safe for sync or async)
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    try {
      setIsSubmitting(true);
      await Promise.resolve(onSubmit && onSubmit());
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditModeChange = async () => {
    try {
      setIsEditingLoading(true);
      await Promise.resolve(onEditModeChange && onEditModeChange());
    } finally {
      setIsEditingLoading(false);
    }
  };

  const handleActionButton = async () => {
    try {
      setIsActionLoading(true);
      await Promise.resolve(onActionButton && onActionButton());
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancelEdit = async () => {
    try {
      setIsEditingLoading(true);
      await Promise.resolve(onCancelEdit && onCancelEdit());
    } finally {
      setIsEditingLoading(false);
    }
  };

  if (!schema) return <Alert severity="info">No schema for this step.</Alert>;

  // Disable grid inputs when not current form and not editing or testing locked
  const gridDisabled = (!isCurrentForm && !isEditMode) || forceDisabled;

  // Show backend action on Testing screen even if it's not the current_form.
  const showActionButton =
    !!actionButtonLabel &&
    (schema.key === "testing" || isCurrentForm) &&
    !isEditMode;

  // Check if we are in retesting mode
  const isRetesting =
    backendCurrentForm === "Flange Retesting" ||
    backendCurrentForm === "Retesting";

  const isStartAction = (actionButtonLabel || "")
    .toLowerCase()
    .includes("start");

  // Show Edit button only when has saved data and not already editing
  const showEditButton =
    !hideEditButton &&
    (!isCurrentForm || forceDisabled) &&
    !isEditMode &&
    hasSavedData &&
    !(isRetesting && isStartAction);

  // Allow "Save Edit" button even if hideSubmit is true (e.g., Testing tab)
  const showSubmitButton =
    !forceDisabled && (isEditMode || (isCurrentForm && !hideSubmit));

  const showReadOnlyHint =
    hasSavedData && !isEditMode && (!isCurrentForm || forceDisabled);
  const readOnlyHint =
    "This completed form is read-only. Select ‘Edit form’ in the header to update remarks or uploads.";

  const lockedTestingHint =
    "Please start testing first. The fields will be editable after testing starts.";

  return (
    <form
      className="flange-dynamic-form"
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Header area */}
      <HeaderRow headerFields={schema.headerFields} headerData={headerData} />

      {/* Bootstrap 5 Grid layout for main fields */}
      <div className="row g-3">
        {schema.fields.map((def, i) => {
          if (
            typeof def.visibleWhen === "function" &&
            !def.visibleWhen(values || {})
          ) {
            return null;
          }

          let colClass = "col-12"; // default full width
          if (def.md === 3) {
            colClass = "col-md-3 col-sm-6 col-12";
          } else if (def.md === 6 || def.grid === 6) {
            colClass = "col-md-4 col-12";
          } else if (def.type === "checkbox") {
            colClass = "col-12 mt-0";
          } else if (def.name === "tightening_remark") {
            colClass = "col-md-4 col-12";
          } else if (def.type === "upload" && isEditMode) {
            colClass = "col-md-12 col-12"; // 50-50 width ONLY in edit mode
          }

          return (
            <Tooltip
              key={def.name || i}
              title={forceDisabled ? lockedTestingHint : showReadOnlyHint ? readOnlyHint : ""}
              placement="top"
              arrow
              enterDelay={300}
              disableHoverListener={!showReadOnlyHint && !forceDisabled}
              disableFocusListener={!showReadOnlyHint && !forceDisabled}
            >
              <div
                className={`${colClass}${showReadOnlyHint ? " flange-readonly-field" : ""}`}
                data-field-name={def.name}
                tabIndex={showReadOnlyHint || forceDisabled ? 0 : undefined}
                onClick={forceDisabled ? onLockedInteraction : undefined}
                onKeyDown={
                  forceDisabled
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onLockedInteraction?.();
                        }
                      }
                    : undefined
                }
                aria-label={
                  forceDisabled
                    ? `${typeof def.label === "function" ? def.label(values || {}) : def.label}. ${lockedTestingHint}`
                    : showReadOnlyHint
                    ? `${typeof def.label === "function" ? def.label(values || {}) : def.label}. ${readOnlyHint}`
                    : undefined
                }
              >
              <Box sx={forceDisabled ? { pointerEvents: "none" } : undefined}>
              <RenderGridField
                def={def}
                values={values}
                onChange={onChange}
                onFieldBlur={onFieldBlur}
                disabled={gridDisabled}
                API_URL={API_URL}
                isEditMode={isEditMode}
                errors={errors}
                fieldAssistMessages={fieldAssistMessages}
                lockTestResultOnly={lockTestResultOnly}
                testingOngoing={testingOngoing}
              />
              </Box>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Actions */}
      {(isSubmitting || isEditingLoading || isActionLoading) && <Loader />}

      <Box
        className="flange-form-actions"
        display="flex"
        justifyContent="flex-end"
        gap={1.25}
      >
        {showEditButton && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditModeChange}
            disabled={isEditingLoading}
          >
            {isEditingLoading ? <CircularProgress size={18} /> : "Edit"}
          </Button>
        )}

        {isEditMode && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<X />}
            onClick={handleCancelEdit}
            disabled={isEditingLoading}
            style={{ width: "100%" }}
          >
            Cancel
          </Button>
        )}

        {showSubmitButton && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : isEditMode ? (
              "Save Edit"
            ) : (
              "Submit"
            )}
          </Button>
        )}

        {showActionButton && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleActionButton}
            disabled={isActionLoading}
          >
            {isActionLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              actionButtonLabel
            )}
          </Button>
        )}
      </Box>
    </form>
  );
}
